/**
 * @file ThemeProvider.jsx
 * @description Design system theme provider.
 *
 * Supports light, dark, high-contrast, and auto (system preference) themes.
 * Field Mode is an independent toggle that increases touch targets and text
 * sizes for outdoor / gloved use.
 *
 * Company branding (primary color, accent color, logo) generates CSS custom
 * property overrides so every component picks them up automatically.
 *
 * Usage:
 *   <DesignThemeProvider theme="auto" fieldMode={false}>
 *     <App />
 *   </DesignThemeProvider>
 *
 *   const { theme, setTheme, fieldMode, toggleFieldMode } = useTheme();
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

const STORAGE_KEY = 'ib-design-theme';
const VALID_THEMES = ['light', 'dark', 'high-contrast', 'auto'];

const ThemeContext = createContext(null);

/**
 * Convert a hex color to HSL components.
 * @param {string} hex - Hex color like "#2563EB"
 * @returns {{ h: number, s: number, l: number }}
 */
function hexToHSL(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 220, s: 80, l: 50 };

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Generate a palette of CSS overrides from a brand primary color.
 * @param {string} primaryColor - Hex color
 * @returns {Object} CSS variable overrides
 */
function generateBrandPalette(primaryColor) {
  if (!primaryColor) return {};
  const { h, s } = hexToHSL(primaryColor);

  return {
    '--color-primary-50':  `hsl(${h}, ${s}%, 97%)`,
    '--color-primary-100': `hsl(${h}, ${s}%, 93%)`,
    '--color-primary-200': `hsl(${h}, ${s}%, 85%)`,
    '--color-primary-300': `hsl(${h}, ${s}%, 73%)`,
    '--color-primary-400': `hsl(${h}, ${s}%, 62%)`,
    '--color-primary-500': `hsl(${h}, ${s}%, 52%)`,
    '--color-primary-600': `hsl(${h}, ${Math.min(s, 85)}%, 45%)`,
    '--color-primary-700': `hsl(${h}, ${Math.min(s, 85)}%, 38%)`,
    '--color-primary-800': `hsl(${h}, ${Math.min(s, 80)}%, 30%)`,
    '--color-primary-900': `hsl(${h}, ${Math.min(s, 80)}%, 24%)`,
    '--color-primary':       primaryColor,
    '--color-primary-hover': `hsl(${h}, ${Math.min(s, 85)}%, 38%)`,
    '--color-primary-active': `hsl(${h}, ${Math.min(s, 80)}%, 30%)`,
    '--color-primary-subtle': `hsl(${h}, ${s}%, 97%)`,
    '--focus-ring':          `0 0 0 3px hsla(${h}, ${s}%, 45%, 0.4)`,
  };
}

/**
 * Load persisted theme preferences from localStorage.
 * @returns {{ theme: string, fieldMode: boolean, companyBrand: Object }}
 */
function loadPersistedPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        theme: VALID_THEMES.includes(parsed.theme) ? parsed.theme : 'auto',
        fieldMode: Boolean(parsed.fieldMode),
        companyBrand: parsed.companyBrand || {}
      };
    }
  } catch {
    // corrupt storage — ignore
  }
  return { theme: 'auto', fieldMode: false, companyBrand: {} };
}

/**
 * Resolve "auto" to the actual theme based on system preference.
 * @param {string} theme
 * @returns {string} 'light' or 'dark'
 */
function resolveAutoTheme(theme) {
  if (theme !== 'auto') return theme;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * @component DesignThemeProvider
 * @param {Object} props
 * @param {'light'|'dark'|'high-contrast'|'auto'} [props.theme] - Initial theme
 * @param {boolean} [props.fieldMode] - Initial field mode state
 * @param {Object} [props.companyBrand] - { primaryColor, accentColor, logo }
 * @param {Function} [props.onThemeSync] - Called with prefs object for server sync
 * @param {React.ReactNode} props.children
 */
export function DesignThemeProvider({
  theme: initialTheme,
  fieldMode: initialFieldMode,
  companyBrand: initialBrand,
  onThemeSync,
  children
}) {
  const persisted = useRef(loadPersistedPrefs());

  const [theme, setThemeState] = useState(
    initialTheme || persisted.current.theme
  );
  const [fieldMode, setFieldMode] = useState(
    initialFieldMode != null ? initialFieldMode : persisted.current.fieldMode
  );
  const [companyBrand, setCompanyBrand] = useState(
    initialBrand || persisted.current.companyBrand
  );

  const resolvedTheme = resolveAutoTheme(theme);

  // Listen for system color scheme changes when in auto mode
  useEffect(() => {
    if (theme !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setThemeState(prev => prev); // force re-render
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // Persist preferences to localStorage
  useEffect(() => {
    const prefs = { theme, fieldMode, companyBrand };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // storage full or blocked — ignore
    }
    // Fire server sync callback if provided
    if (onThemeSync) {
      onThemeSync(prefs);
    }
  }, [theme, fieldMode, companyBrand, onThemeSync]);

  // Apply data attributes to document root
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-theme', resolvedTheme);
    if (fieldMode) {
      html.setAttribute('data-mode', 'field');
    } else {
      html.removeAttribute('data-mode');
    }
    return () => {
      html.removeAttribute('data-theme');
      html.removeAttribute('data-mode');
    };
  }, [resolvedTheme, fieldMode]);

  const setTheme = useCallback((newTheme) => {
    if (VALID_THEMES.includes(newTheme)) {
      setThemeState(newTheme);
    }
  }, []);

  const toggleFieldMode = useCallback(() => {
    setFieldMode(prev => !prev);
  }, []);

  const updateCompanyBrand = useCallback((brand) => {
    setCompanyBrand(prev => ({ ...prev, ...brand }));
  }, []);

  // Generate CSS overrides from company brand
  const brandOverrides = useMemo(() => {
    return generateBrandPalette(companyBrand.primaryColor);
  }, [companyBrand.primaryColor]);

  const contextValue = useMemo(() => ({
    theme,
    resolvedTheme,
    setTheme,
    fieldMode,
    toggleFieldMode,
    setFieldMode,
    companyBrand,
    updateCompanyBrand
  }), [theme, resolvedTheme, setTheme, fieldMode, toggleFieldMode, companyBrand, updateCompanyBrand]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <div className="ib-root" style={brandOverrides}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

/**
 * Default theme context returned when no DesignThemeProvider is in the tree.
 *
 * Returning a safe default (instead of throwing) lets components like the
 * Navbar call useTheme() unconditionally — which is required by the
 * Rules of Hooks. Public marketing pages, error boundaries, isolated
 * tests, and any tree rendered before the provider mounts will all
 * receive this no-op shape rather than crash.
 */
const DEFAULT_THEME_CONTEXT = Object.freeze({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
  fieldMode: false,
  toggleFieldMode: () => {},
  setFieldMode: () => {},
  companyBrand: {},
  updateCompanyBrand: () => {},
});

/**
 * Hook to access theme context.
 *
 * If called outside a DesignThemeProvider, returns DEFAULT_THEME_CONTEXT
 * (a frozen no-op shape) so the call site can be unconditional and
 * Rules-of-Hooks compliant. The provider is mounted at the App root,
 * so in practice this fallback only fires for SSR, isolated tests,
 * or any future tree rendered outside the provider.
 *
 * @returns {{
 *   theme: string,
 *   resolvedTheme: string,
 *   setTheme: Function,
 *   fieldMode: boolean,
 *   toggleFieldMode: Function,
 *   setFieldMode: Function,
 *   companyBrand: Object,
 *   updateCompanyBrand: Function
 * }}
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  return ctx || DEFAULT_THEME_CONTEXT;
}

export default DesignThemeProvider;
